import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Link from 'next/link';

import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';
import { hexToRGBA } from '_helpers/theme';
import { getBlogArticle, getBlogImageAlt } from '_helpers/blog';

import * as Layout from '_components/atomic/layouts';

const PALETTE = [
  theme.colors.green[500],
  theme.colors.orange[500],
  theme.colors.blue[500],
  theme.colors.gray[500],
  theme.colors.indigo[500],
];

function reduceText(text, len = 100) {
  return `${text
    ?.slice(0, len + 1)
    ?.trim()
    ?.replace(/\.$/, '')}...`;
}

const StyledContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: ${theme.spaces.large2X}px;

  @media ${theme.devices.tablet} {
    grid-template-columns: auto;
    grid-gap: ${theme.spaces.large}px;
  }
`;

const StyledBodyContainer = styled.div``;

const StyledArticleContainer = styled.div`
  @media ${theme.devices.tablet} {
    display: flex;
  }
`;

const StyledImageContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: ${theme.spaces.large}px;

  picture {
    width: 100%;

    img {
      height: 200px;
      width: 100%;
      object-fit: cover;
      border-radius: 5px;
    }
  }

  @media ${theme.devices.tablet} {
    align-items: flex-start;
    margin-right: ${theme.spaces.smallX}px;

    picture {
      width: unset;

      img {
        height: 80px;
        width: 80px;
      }
    }
  }
`;

const StyledTagsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: ${theme.spaces.smallX}px;
  margin-bottom: ${theme.spaces.small}px;
`;

const StyledTag = styled.div`
  padding: ${theme.spaces.small2X}px;
  border-radius: 6px;
  background: ${hexToRGBA(theme.colors.green[500], 0.1)};
  font-family: 'Aeonik';
  font-size: ${theme.fontSizes.small}px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
  color: ${theme.colors.green[500]};

  ${({ color }) =>
    color &&
    css`
      background: ${hexToRGBA(color, 0.1)};
      color: ${color};
    `}
`;

const StyledLink = styled(Link)`
  font-family: inherit;
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: inherit;
  }
`;

const StyledBottomLinkContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${theme.spaces.medium}px;

  a {
    font-family: inherit;
    font-size: ${theme.fontSizes.small}px;
    color: ${theme.colors.indigo[600]};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
      color: inherit;
    }
  }

  @media ${theme.devices.tablet} {
    display: none;
  }
`;

const Articles = ({ subRoutes }) => {
  return (
    <StyledContainer>
      {subRoutes.map((subRoute) => {
        const { title, description, imgSrc, timeToReadMin, timestamp, tags } =
          getBlogArticle(subRoute) || {};

        const imgAlt = getBlogImageAlt(subRoute);

        return (
          <StyledArticleContainer key={subRoute}>
            <StyledImageContainer>
              <picture>
                <source srcSet={imgSrc} type="image/svg+xml" />
                <img src={imgSrc} alt={imgAlt} />
              </picture>
            </StyledImageContainer>
            <StyledBodyContainer>
              <StyledTagsContainer>
                {tags.map((tag) => (
                  <StyledTag
                    key={`${subRoute}:${tag}`}
                    color={
                      PALETTE[Math.round((PALETTE.length - 1) * Math.random())]
                    }
                  >
                    {tag}
                  </StyledTag>
                ))}
              </StyledTagsContainer>
              <Layout.Card.Text
                desktop
                size={theme.fontSizes.medium}
                boldest
                noBottomSpace
              >
                <StyledLink href={`${ROUTES.blog}${subRoute}`}>
                  {title}
                </StyledLink>
              </Layout.Card.Text>
              <Layout.Card.Text
                mobile
                size={theme.fontSizes.regular}
                boldest
                noBottomSpace
              >
                <StyledLink href={`${ROUTES.blog}${subRoute}`}>
                  {title}
                </StyledLink>
              </Layout.Card.Text>
              <Layout.Card.Text size={theme.fontSizes.regular} desktop>
                {reduceText(description)}
              </Layout.Card.Text>
              <Layout.Card.Text size={theme.fontSizes.regular} mobile>
                {reduceText(description)}
              </Layout.Card.Text>
              <Layout.Card.Text
                size={theme.fontSizes.small}
                color={theme.colors.gray[500]}
                desktop
              >
                Updated on: {timestamp} · {timeToReadMin} min read
              </Layout.Card.Text>
              <Layout.Card.Text
                size={theme.fontSizes.small}
                color={theme.colors.gray[500]}
                mobile
              >
                Updated on: {timestamp} · {timeToReadMin} min read
              </Layout.Card.Text>
              <StyledBottomLinkContainer>
                <Link href={`${ROUTES.blog}${subRoute}`}>Learn more</Link>
              </StyledBottomLinkContainer>
            </StyledBodyContainer>
          </StyledArticleContainer>
        );
      })}
    </StyledContainer>
  );
};

Articles.propTypes = {
  subRoutes: PropTypes.array,
};

export default Articles;
